import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { FormLabel } from "../../components/form-label/FormLabel";
import { tenantService } from "../../services/tenantService";
import { flowService } from "../../services/flowService";
import { CollectedData, Flow, Tenant } from "../../types";

interface CollectedDoc extends CollectedData {
  id: string;
}

export default function CollectedDataList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";
  const flowIdParam = searchParams.get("flowId") ?? "";

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [records, setRecords] = useState<CollectedDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load tenants
  useEffect(() => {
    tenantService
      .list()
      .then((res) => setTenants(res.data.data.tenants))
      .catch((e) => console.error(e));
  }, []);

  // Load flows when tenant changes
  useEffect(() => {
    if (!tenantIdParam) {
      setFlows([]);
      return;
    }
    flowService
      .list(tenantIdParam)
      .then((res) => setFlows(res.data.data.flows))
      .catch((e) => console.error(e));
  }, [tenantIdParam]);

  // Firestore snapshot
  useEffect(() => {
    if (!tenantIdParam) {
      setRecords([]);
      return;
    }
    setLoading(true);
    setError(null);

    const constraints: QueryConstraint[] = [
      where("tenant_id", "==", tenantIdParam),
    ];
    if (flowIdParam) constraints.push(where("flow_id", "==", flowIdParam));
    constraints.push(orderBy("completed_at", "desc"), limit(100));

    const q = query(collection(db, "collected_data"), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRecords(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as CollectedData) }))
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Errore nel caricamento dei dati raccolti");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [tenantIdParam, flowIdParam]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const formatDate = (ts: { seconds: number } | null) =>
    ts ? new Date(ts.seconds * 1000).toLocaleString("it-IT") : "—";

  const flowName = (id: string) => flows.find((f) => f.id === id)?.name ?? id.slice(0, 8) + "…";

  const previewData = (data: Record<string, unknown>) => {
    const keys = Object.keys(data).slice(0, 3);
    return keys.map((k) => `${k}: ${String(data[k])}`).join(" · ");
  };

  return (
    <div className="container">
      <FormLabel label="Dati Raccolti" />

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="tenant-filter">Tenant</InputLabel>
          <Select
            labelId="tenant-filter"
            label="Tenant"
            value={tenantIdParam}
            onChange={(e) => {
              updateParam("tenantId", e.target.value);
              updateParam("flowId", "");
            }}
          >
            <MenuItem value="">— seleziona tenant —</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!tenantIdParam}>
          <InputLabel id="flow-filter">Flow (opzionale)</InputLabel>
          <Select
            labelId="flow-filter"
            label="Flow (opzionale)"
            value={flowIdParam}
            onChange={(e) => updateParam("flowId", e.target.value)}
          >
            <MenuItem value="">Tutti i flow</MenuItem>
            {flows.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!tenantIdParam ? (
        <Alert severity="info">Seleziona un tenant per visualizzare i dati.</Alert>
      ) : loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Utente (telefono)</TableCell>
                <TableCell>Flow</TableCell>
                <TableCell>Completato il</TableCell>
                <TableCell>Dati (anteprima)</TableCell>
                <TableCell align="right">Dettaglio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nessun dato raccolto trovato
                  </TableCell>
                </TableRow>
              )}
              {records.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {r.user_phone}
                    </Typography>
                  </TableCell>
                  <TableCell>{flowName(r.flow_id)}</TableCell>
                  <TableCell>{formatDate(r.completed_at)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {previewData(r.data)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Vedi dettaglio">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/chatbot/monitoring/collected/${encodeURIComponent(r.id)}?tenantId=${tenantIdParam}`
                          )
                        }
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
